import axios, { AxiosInstance } from 'axios';
import { useAuthStore } from '../store/authStore';

const apiUrl = (import.meta as any).env.VITE_API_URL || 'http://localhost:3001/api';

export const api: AxiosInstance = axios.create({
  baseURL: apiUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any = null, token: string | null = null) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token);
    }
  });
  failedQueue = [];
};

// Request interceptor to add token
api.interceptors.request.use((config) => {
  const tokens = useAuthStore.getState().tokens;
  if (tokens?.accessToken) {
    config.headers.Authorization = `Bearer ${tokens.accessToken}`;
    console.log('✅ Authorization header added:', `Bearer ${tokens.accessToken.substring(0, 20)}...`);
  } else {
    console.log('⚠️ No access token found in authStore');
  }
  return config;
});

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      const tokens = useAuthStore.getState().tokens;
      const refreshToken = tokens?.refreshToken;

      if (!refreshToken) {
        console.log('❌ No refresh token available, logging out');
        useAuthStore.getState().logout();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        console.log('⏳ Already refreshing, queuing request');
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        console.log('🔄 Attempting to refresh token...');
        const response = await axios.post(`${apiUrl}/auth/refresh`, {
          refreshToken: refreshToken,
        });

        const newTokens = response.data.data || response.data;
        console.log('✅ Token refreshed successfully');
        
        useAuthStore.getState().setTokens(newTokens);
        originalRequest.headers.Authorization = `Bearer ${newTokens.accessToken}`;
        
        processQueue(null, newTokens.accessToken);
        return api(originalRequest);
      } catch (refreshError: any) {
        console.error('❌ Token refresh failed:', refreshError.response?.data || refreshError.message);
        processQueue(refreshError, null);
        useAuthStore.getState().logout();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;
